import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TemplatePageModel, TemplatePageRequestOptions } from '@models/types'
import useDispatch from '@hooks/useDispatch'

import * as templatePageActions from '@redux/modules/templatePage'

const watchEntityKeys = ['templatePages']

const syncBrandTemplates = (params: any) => {
  const { dispatch } = params
  const { syncBrandTemplates: syncBrandTemplatesFn } = templatePageActions

  return dispatch(syncBrandTemplatesFn())
}

type TemplatePageFilters = {
  agency_id?: number | string,
  brand_id?: number | string,
  brand_theme_id?: number | string,
  builder?: string,
  hidden?: boolean,
  provider_id?: number | string,
  publication_id?: number | string,
  service_id?: number | string,
  service_product_id?: number | string,
  string?: string,
  template_group_id?: number | string,
}

type UseTemplatePagesOptions = {
  filters?: TemplatePageFilters,
  performHttpRequests?: boolean,
  requestOptions?: TemplatePageRequestOptions,
}

function useTemplatePages(options: UseTemplatePagesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    brand_id: filterBrandId,
    brand_theme_id: filterBrandThemeId,
    builder: filterBuilder,
    hidden: filterHidden,
    provider_id: filterProviderId,
    publication_id: filterPublicationId,
    service_id: filterServiceId,
    service_product_id: filterServiceProductId,
    string: filterString,
    template_group_id: filterTemplateGroupId,
  } = filters

  const {
    updatedEntities: { templatePages: templatePagesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { templatePages } = useSelector(reduxState => reduxState.entities)

  const dispatch = useDispatch()

  // Filter redux entities with provided filters
  const filteredTemplatePages = useMemo(() => {
    const filtered = Object.values(templatePages).filter((templatePage: TemplatePageModel) => {
      const {
        brand_id, builder, hidden, id, permission_ids, service_id, title,
      } = templatePage

      const {
        agencies: agencyIds = [],
        brand_themes: brandThemeIds = [],
        products: serviceProductIds = [],
        providers: providerIds = [],
        publications: publicationIds = [],
        template_groups: templateGroupIds = [],
      } = permission_ids ?? {}

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle
      const matchBuilder = matchFilterString(builder, filterBuilder)

      const matchAgencyId = matchFilterArrayIncludes(agencyIds, filterAgencyId ? Number(filterAgencyId) : undefined)
      // @ts-expect-error TS2345
      const matchBrandId = matchFilterNumber(brand_id, filterBrandId)
      const matchBrandThemeId = matchFilterArrayIncludes(
        brandThemeIds,
        filterBrandThemeId ? Number(filterBrandThemeId) : undefined,
      )
      const matchHidden = filterHidden === hidden
      const matchProviderId = matchFilterArrayIncludes(
        providerIds,
        filterProviderId ? Number(filterProviderId) : undefined,
      )
      const matchPublicationId = matchFilterArrayIncludes(
        publicationIds,
        filterPublicationId ? Number(filterPublicationId) : undefined,
      )
      // @ts-expect-error TS2345
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)
      const matchServiceProductId = matchFilterArrayIncludes(
        serviceProductIds,
        filterServiceProductId ? Number(filterServiceProductId) : undefined,
      )
      const matchTemplateGroupId = matchFilterArrayIncludes(
        templateGroupIds,
        filterTemplateGroupId ? Number(filterTemplateGroupId) : undefined,
      )

      return (
        matchAgencyId
        && matchBrandId
        && matchBrandThemeId
        && matchBuilder
        && matchHidden
        && matchProviderId
        && matchPublicationId
        && matchServiceId
        && matchServiceProductId
        && matchString
        && matchTemplateGroupId
      )
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [templatePagesUpdatedAt, JSON.stringify(filters)])

  const filteredTemplatePagesCount = filteredTemplatePages.length
  const hasFilteredTemplatePages = !!filteredTemplatePagesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTemplatePagesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingTemplatePages } = useReduxAction(
    'templatePages',
    'loadTemplatePages',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
      syncBrandTemplates: () => {
        syncBrandTemplates({dispatch})
      }
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading: loadingTemplatePages,
  }
}

export default useTemplatePages
